import type { AssistantError, RunEvent } from '@agently/types';
import { camelizeKeys } from '@agently/utils';

import { getAuthToken, handleUnauthorized } from '../auth-token';
import { ASSISTANT_URL } from './client';
import {
  assistantErrorFromBody,
  isAbortError,
  parseAssistantError,
} from './errors';

// The server pings every 15 s: three missed pings mean the connection is dead.
const WATCHDOG_MS = 45_000;

/**
 * Server-sent events framing: yields the `data` of each event. Handles `:`
 * comments (pings), CRLF, multi-line data and chunk boundaries anywhere,
 * including inside a UTF-8 sequence.
 */
async function* sseData(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onBytes?: () => void,
): AsyncGenerator<string> {
  const decoder = new TextDecoder();
  let buffer = '';
  let data: string[] = [];

  const readLine = (line: string): string | undefined => {
    if (line === '') {
      const event = data.length > 0 ? data.join('\n') : undefined;
      data = [];
      return event;
    }
    if (line.startsWith('data:')) {
      data.push(line.slice(line.startsWith('data: ') ? 6 : 5));
    }
    return undefined;
  };

  while (true) {
    const { done, value } = await reader.read();
    onBytes?.();
    buffer += decoder.decode(value, { stream: !done });
    // A lone trailing "\r" may be the first half of "\r\n": keep it for the next read.
    const held = !done && buffer.endsWith('\r') ? '\r' : '';
    const lines = buffer
      .slice(0, buffer.length - held.length)
      .split(/\r\n|\n|\r/);
    buffer = (lines.pop() ?? '') + held;
    for (const line of lines) {
      const event = readLine(line);
      if (event !== undefined) {
        yield event;
      }
    }
    if (done) {
      const event = readLine(buffer) ?? readLine('');
      if (event !== undefined) {
        yield event;
      }
      return;
    }
  }
}

const isTerminal = (event: RunEvent) =>
  event.type === 'RUN_FINISHED' || event.type === 'RUN_ERROR';

function parseEvent(raw: string): RunEvent {
  let event: any;
  try {
    event = JSON.parse(raw);
  } catch {
    throw {
      code: 'UNKNOWN',
      message: 'Malformed stream event',
    } satisfies AssistantError;
  }
  if (event?.type === 'TEXT_MESSAGE_END' && event.metadata) {
    event.metadata = camelizeKeys(event.metadata);
  }
  return event as RunEvent;
}

/**
 * Streams one Run. Failures are thrown as `AssistantError`s; an abort by the
 * caller is rethrown as the original `AbortError`.
 */
export async function* streamRun({
  threadId,
  question,
  signal,
}: {
  threadId: string;
  question: string;
  signal: AbortSignal;
}): AsyncGenerator<RunEvent> {
  const controller = new AbortController();
  const abort = () => controller.abort(signal.reason);
  if (signal.aborted) {
    abort();
  }
  signal.addEventListener('abort', abort);

  let idle = false;
  // Once the event stream has opened, any failure is an interruption, not a refusal to start.
  let opened = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const kick = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      idle = true;
      controller.abort();
    }, WATCHDOG_MS);
  };
  kick();

  try {
    const token = getAuthToken();
    const response = await fetch(`${ASSISTANT_URL}/runs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        threadId,
        runId: crypto.randomUUID(),
        messages: [{ role: 'user', content: question }],
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
      }
      const body = await response.json().catch(() => undefined);
      throw assistantErrorFromBody(response.status, body);
    }
    if (!response.headers.get('Content-Type')?.includes('text/event-stream')) {
      throw {
        code: 'UNKNOWN',
        message: 'Unexpected response',
        status: response.status,
      } satisfies AssistantError;
    }
    if (!response.body) {
      throw { code: 'NETWORK', message: 'Empty response' };
    }

    opened = true;
    const reader = response.body.getReader();
    for await (const raw of sseData(reader, kick)) {
      const event = parseEvent(raw);
      yield event;
      if (isTerminal(event)) {
        return;
      }
    }
    throw {
      code: 'NETWORK',
      message: 'The stream ended before the Run finished',
    } satisfies AssistantError;
  } catch (error) {
    const phase = opened ? 'stream' : 'pre-stream';
    if (idle) {
      throw { code: 'NETWORK', message: 'The stream went silent', phase };
    }
    if (isAbortError(error)) {
      throw error;
    }
    throw { ...parseAssistantError(error), phase };
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
    controller.abort();
  }
}
